import React, { useEffect } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Alert, Button, Box, Toolbar } from '@mui/material';

import theme from './theme';
import ErrorBoundary from './components/ErrorBoundary';
import WebAuthGate from './components/WebAuthGate';
import LicensePrompt from './components/LicensePrompt';
import LicenseBanner from './components/LicenseBanner';
import UpdateNotifier from './components/UpdateNotifier';
import Navigation from './components/Navigation';
import Dashboard from './pages/Dashboard';
import BirdList from './pages/BirdList';
import BirdProfile from './pages/BirdProfile';
import AddBird from './pages/AddBird';
import PedigreeView from './pages/PedigreeView';
import SeasonPerformance from './pages/SeasonPerformance';
import WeeklyDiary from './pages/WeeklyDiary';
import Settings from './pages/Settings';
import TestPedigrees from './pages/TestPedigrees';
import SharedWithMe from './pages/SharedWithMe';
import PdfHistory from './pages/PdfHistory';
import { EducationArticle, ResourceHub } from './pages/PublicResources';
import { IS_WEB } from './services/platform';
import { LicenseProvider, useLicense } from './context/LicenseContext';

import './App.css';

function AppContent() {
  const { state, reload } = useLicense();
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  if (!state) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <div>Loading...</div>
      </Box>
    );
  }

  if (state.mode === 'error') {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', p: 3, pt: 8 }}>
        <Alert severity="warning">We couldn’t check your trial or plan. Check your connection and try again.</Alert>
        <Button variant="contained" onClick={reload} sx={{ mt: 2, minHeight: 44 }}>Try again</Button>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Navigation />
        {/* minWidth: 0 lets wide content (tables, pedigree grids) scroll inside
            the main column instead of stretching the page on small screens. */}
        <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Spacer for the mobile top bar (hidden on md+, where the
              permanent sidebar renders instead). */}
          <Toolbar sx={{ display: { xs: 'block', md: 'none' } }} />
          <LicenseBanner />
          <Box sx={{ flexGrow: 1, p: 3, pt: { xs: 2, md: 3 }, px: { xs: 2, md: 3 } }}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/birds" element={<BirdList />} />
              <Route path="/birds/add" element={<AddBird />} />
              <Route path="/birds/:id/edit" element={<AddBird />} />
              <Route path="/birds/:id" element={<BirdProfile />} />
              <Route path="/pedigree/:id" element={<PedigreeView />} />
              <Route path="/season" element={<SeasonPerformance />} />
              <Route path="/diary" element={<WeeklyDiary />} />
              <Route path="/test-pedigrees" element={<TestPedigrees />} />
              {IS_WEB && <Route path="/downloads" element={<PdfHistory />} />}
              {IS_WEB && <Route path="/shared" element={<SharedWithMe />} />}
              <Route path="/settings" element={<Settings />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Box>
        </Box>
      </Box>
      <LicensePrompt />
      <UpdateNotifier />
    </>
  );
}

function App() {
  // Clean URLs make public guides indexable on the web. Electron keeps hash
  // routing because its pages are loaded from the local file system.
  const Router = IS_WEB ? BrowserRouter : HashRouter;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <Router>
          <Routes>
            <Route path="/resources" element={<ResourceHub />} />
            <Route path="/guides/:slug" element={<EducationArticle />} />
            <Route
              path="*"
              element={(
                <WebAuthGate>
                  <LicenseProvider>
                    <AppContent />
                  </LicenseProvider>
                </WebAuthGate>
              )}
            />
          </Routes>
        </Router>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
